import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Play, 
  Pause, 
  Settings, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Activity,
  BarChart3,
  Clock,
  Target,
  ExternalLink,
  Plus,
  Edit,
  Trash2,
  Users
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();
const LOG_FETCH_LIMIT = 300;

const MarketMaker = () => {
  // Config state
  const [config, setConfig] = useState({
    enabled: false,
    min_enemy_volume_usd: 10.0,
    reaction_delay_seconds: 5.0,
    enemy_buy_sell_min_percent: 50.0,
    enemy_buy_sell_max_percent: 100.0,
    enemy_sell_buy_min_percent: 50.0,
    enemy_sell_buy_max_percent: 100.0,
    split_threshold_usd: 50.0,
    max_per_operation_usd: 1000.0,
    // Price Filter
    enable_price_filter: false,
    min_sell_price_increase_percent: 2.0,
    min_buy_price_decrease_percent: 2.0,
    // Grid Trading
    enable_grid_trading: false,
    grid_trading_mode: 'BOTH', // BOTH, BUY_ONLY, SELL_ONLY
    grid_sell_interval_percent: 5.0,
    grid_sell_amount_percent: 10.0,
    grid_buy_interval_percent: 5.0,
    grid_buy_amount_usd: 100.0,
    // SELL_ONLY mode settings
    grid_sell_on_enemy_buy: false,
    grid_sell_percent_of_enemy: 50.0,
    grid_sell_price_change_percent: 5.0,
    // Min transaction size
    min_transaction_multiplier: 6,
    // Grid Trading wallet selection
    grid_trading_wallet_addresses: []
  });

  // Status state
  const [status, setStatus] = useState({
    running: false,
    enabled: false
  });

  // Stats state
  const [stats, setStats] = useState({
    total_operations: 0,
    successful_operations: 0,
    failed_operations: 0,
    total_volume_usd: 0,
    total_pnl_usd: 0,
    buys: 0,
    sells: 0
  });

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('strategy'); // strategy, priceHolding, logs, gridTrading
  const [logs, setLogs] = useState([]);
  const [randomConfig, setRandomConfig] = useState(null);
  const [randomStatus, setRandomStatus] = useState(null);
  const [randomLoading, setRandomLoading] = useState(false);
  const [randomSaving, setRandomSaving] = useState(false);
  
  // Strategies state (multiple parallel strategies)
  const [strategies, setStrategies] = useState([]);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [showStrategyDialog, setShowStrategyDialog] = useState(false);
  const [walletGroups, setWalletGroups] = useState([]);
  
  // Grid Trading series state
  const [seriesStatus, setSeriesStatus] = useState({
    series_id: null,
    status: 'NONE',
    base_price_usd: null,
    last_sell_price: null,
    last_buy_price: null,
    total_buys: 0,
    total_sells: 0,
    profit_loss_usd: 0
  });
  const [seriesHistory, setSeriesHistory] = useState([]);
  const [availableWallets, setAvailableWallets] = useState([]);

  // Price Holding state
  const [priceHoldingConfig, setPriceHoldingConfig] = useState({
    enabled: false,
    min_price_usd: 0.00012,
    buy_volume_percent: 50.0,
    max_total_buy_usd: 500.0,
    stop_at_floor: true,
    max_price_usd: 0.00015,
    sell_levels: 4,
    sell_volume_percent: 25.0,
    max_total_sell_percent: 80.0,
    check_interval_seconds: 30.0,
    react_delay_seconds: 5.0
  });

  const [priceHoldingStatus, setPriceHoldingStatus] = useState({
    current_price: 0,
    at_floor: false,
    at_ceiling: false
  });

  useEffect(() => {
    // Load critical data first (config, status)
    loadConfig();
    loadStatus();
    loadStats();
    
    // Load history with loading indicator on initial load (WITH real-time for immediate display)
    loadHistory(true, false);  // CRITICAL: Load real-time data immediately
    loadRandomConfig();
    loadRandomStatus();
    
    // Load other data in parallel (non-blocking)
    Promise.all([
      loadSeriesStatus(),
      loadSeriesHistory(),
      loadWallets(),
      loadStrategies(),
      loadWalletGroups()
    ]).catch(err => console.error('Error loading initial data:', err));

    // Refresh status and stats every 0.5 seconds для обновлений в реальном времени (reduced from 1s)
    // История обновляется каждую 0.5 секунды для максимально быстрого отображения свапов
    const interval = setInterval(() => {
      loadStatus();
      loadStats();
      // Обновляем историю каждую 0.5 секунды для реального времени (без индикатора загрузки)
      loadHistory(false, false);  // CRITICAL: Always load real-time data
      loadSeriesStatus();
      if (activeTab === 'gridTrading') {
        loadSeriesHistory();
      }
      if (activeTab === 'strategy') {
        loadStrategies();
      }
    }, 1000);  // CRITICAL: Reduced from 3000ms to 1000ms for faster updates

    return () => clearInterval(interval);
  }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadRandomStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/config`);
      if (response.data.success) {
        const loadedConfig = response.data.config;
        // Merge with defaults to ensure all fields are present
        setConfig(prevConfig => ({
          ...prevConfig,
          ...loadedConfig,
          // Ensure new Grid Trading fields have defaults
          grid_trading_mode: loadedConfig.grid_trading_mode ?? 'BOTH',
          grid_sell_on_enemy_buy: loadedConfig.grid_sell_on_enemy_buy ?? false,
          grid_sell_percent_of_enemy: loadedConfig.grid_sell_percent_of_enemy ?? 50.0,
          grid_sell_price_change_percent: loadedConfig.grid_sell_price_change_percent ?? 5.0,
        }));
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadStrategies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/strategies`);
      const data = response?.data ?? {};
      // Some deployments may omit the success flag; accept data when array is present
      const strategiesList = Array.isArray(data?.strategies) ? data.strategies : (Array.isArray(data) ? data : []);
      console.log('Loaded strategies:', strategiesList.length);
      setStrategies(strategiesList);
      return strategiesList;
    } catch (error) {
      console.error('Error loading strategies:', error);
      return [];
    }
  };

  const loadWalletGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/groups`);
      console.log('Groups API response:', response.data);
      // API returns { count, groups } - no 'success' field
      if (response.data && response.data.groups) {
        const groups = response.data.groups;
        console.log('Loaded groups:', groups.length, groups.map(g => g.group_name || g));
        setWalletGroups(groups);
      } else if (response.data && Array.isArray(response.data)) {
        // If API returns array directly
        setWalletGroups(response.data);
      } else {
        console.warn('Unexpected groups API response format:', response.data);
      }
    } catch (error) {
      console.error('Error loading wallet groups:', error);
      // Fallback: try to get groups from wallets list
      try {
        const walletsResponse = await axios.get(`${API_URL}/api/wallets/list`);
        if (walletsResponse.data && walletsResponse.data.wallets) {
          const uniqueGroups = [...new Set(walletsResponse.data.wallets.map(w => w.group_name).filter(Boolean))];
          console.log('Fallback: loaded groups from wallets:', uniqueGroups);
          setWalletGroups(uniqueGroups);
        }
      } catch (fallbackError) {
        console.error('Fallback error loading groups:', fallbackError);
      }
    }
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/status`);
      if (response.data.success) {
        setStatus({
          running: response.data.running,
          enabled: response.data.enabled
        });
      }
    } catch (error) {
      console.error('Error loading status:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/stats`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadHistory = async (showLoading = false, skipRealtime = false) => {
    try {
      if (showLoading) {
        setHistoryLoading(true);
      }
      // OPTIMIZATION: Skip real-time fetch on initial load for faster response
      const response = await axios.get(`${API_URL}/api/market-maker/history?limit=50&skip_realtime=${skipRealtime}`);
      if (response.data) {
        const newHistory = response.data.history || [];
        
        // Умное обновление истории: избегаем "прыжков" при обновлении
        setHistory(prevHistory => {
          // Если нет предыдущих данных, устанавливаем новые
          if (!prevHistory || prevHistory.length === 0) {
            return newHistory;
          }
          
          // Если новые данные пусты, сохраняем предыдущие (не очищаем историю при временных сбоях)
          if (newHistory.length === 0) {
            return prevHistory;
          }
          
          // Создаем карту предыдущих записей по ключу (id или tx_hash) для быстрого поиска
          const prevMap = new Map();
          prevHistory.forEach(entry => {
            const key = entry.id || entry.enemy_tx_hash || entry.our_tx_hashes?.[0];
            if (key) {
              prevMap.set(key, entry);
            }
          });
          
          // Проверяем, есть ли реальные изменения
          let hasChanges = false;
          let hasNewEntries = false;
          
          // Проверяем новую первую запись (самая новая)
          const newFirst = newHistory[0];
          const prevFirst = prevHistory[0];
          
          if (newFirst) {
            const newKey = newFirst.id || newFirst.enemy_tx_hash || newFirst.our_tx_hashes?.[0];
            const prevKey = prevFirst?.id || prevFirst?.enemy_tx_hash || prevFirst?.our_tx_hashes?.[0];
            
            // Если появилась новая запись в начале
            if (newKey !== prevKey) {
              hasNewEntries = true;
            }
          }
          
          // Если длина изменилась, значит что-то добавилось или удалилось
          if (prevHistory.length !== newHistory.length) {
            hasChanges = true;
          }
          
          // Проверяем первые несколько записей на изменения в ключевых полях
          const checkCount = Math.min(5, Math.min(prevHistory.length, newHistory.length));
          for (let i = 0; i < checkCount; i++) {
            const prevEntry = prevHistory[i];
            const newEntry = newHistory[i];
            
            if (!prevEntry || !newEntry) {
              hasChanges = true;
              break;
            }
            
            // Проверяем ключевые поля, которые могут измениться
            if (prevEntry.id !== newEntry.id ||
                prevEntry.enemy_tx_hash !== newEntry.enemy_tx_hash ||
                prevEntry.status !== newEntry.status ||
                prevEntry.our_volume_usd !== newEntry.our_volume_usd ||
                prevEntry.enemy_volume_usd !== newEntry.enemy_volume_usd) {
              hasChanges = true;
              break;
            }
          }
          
          // Обновляем только если есть реальные изменения
          if (hasChanges || hasNewEntries) {
            return newHistory;
          }
          
          // Если изменений нет, возвращаем предыдущие данные для стабильности
          return prevHistory;
        });
      }
    } catch (error) {
      console.error('Error loading history:', error);
      // НЕ очищаем историю при ошибке, чтобы она не пропадала
      // История останется в предыдущем состоянии
    } finally {
      if (showLoading) {
        setHistoryLoading(false);
      }
    }
  };

  const loadRandomConfig = async () => {
    setRandomLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/random-config`);
      if (response.data.success) {
        setRandomConfig(response.data.config);
      } else {
        setRandomConfig(null);
      }
    } catch (error) {
      console.error('Error loading random config:', error);
      setRandomConfig(null);
    } finally {
      setRandomLoading(false);
    }
  };

  const saveRandomConfig = async () => {
    if (!randomConfig) return;
    setRandomSaving(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/random-config`, randomConfig);
      toast.success('Random configuration saved');
      await loadRandomConfig();
      await loadRandomStatus();
    } catch (error) {
      console.error('Error saving random config:', error);
      toast.error('Failed to save random configuration');
    } finally {
      setRandomSaving(false);
    }
  };

  const loadRandomStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/random-status`);
      if (response.data.success) {
        setRandomStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error loading random status:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/logs`, {
        params: { limit: LOG_FETCH_LIMIT }
      });
      if (response.data.success) {
        const normalizedLogs = (response.data.logs || []).map((log, index) => {
          if (typeof log === 'string') {
            const match = log.match(/^\[(\w+)\]\s+(.*)$/);
            return {
              id: `legacy-${index}`,
              timestamp: null,
              level: match?.[1] || 'INFO',
              message: match?.[2] || log
            };
          }
          return {
            id: log.id ?? `${log.timestamp ?? 'log'}-${index}`,
            timestamp: log.timestamp || null,
            level: log.level || 'INFO',
            message: log.message || ''
          };
        });
        setLogs(normalizedLogs);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  // Grid Trading functions
  const loadSeriesStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/series/status`);
      if (response.data.success) {
        setSeriesStatus(response.data.series);
      }
    } catch (error) {
      console.error('Error loading series status:', error);
    }
  };

  const loadSeriesHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/series/history?limit=50`);
      if (response.data.success) {
        setSeriesHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error loading series history:', error);
    }
  };

  const startSeries = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/market-maker/series/start`);
      if (response.data.success) {
        toast.success('Trading series started');
        await loadSeriesStatus();
        await loadSeriesHistory();
      }
    } catch (error) {
      console.error('Error starting series:', error);
      toast.error('Failed to start series');
    }
  };

  const stopSeries = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/market-maker/series/stop`);
      if (response.data.success) {
        toast.success('Trading series stopped');
        await loadSeriesStatus();
      }
    } catch (error) {
      console.error('Error stopping series:', error);
      toast.error('Failed to stop series');
    }
  };

  const loadWallets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/list`);
      if (response.data.wallets) {
        setAvailableWallets(response.data.wallets);
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
    }
  };

  const toggleWalletSelection = (address) => {
    const current = config.grid_trading_wallet_addresses || [];
    if (current.includes(address)) {
      setConfig({
        ...config,
        grid_trading_wallet_addresses: current.filter(a => a !== address)
      });
    } else {
      setConfig({
        ...config,
        grid_trading_wallet_addresses: [...current, address]
      });
    }
  };

  const selectAllWallets = () => {
    setConfig({
      ...config,
      grid_trading_wallet_addresses: availableWallets.map(w => w.address)
    });
  };

  const deselectAllWallets = () => {
    setConfig({
      ...config,
      grid_trading_wallet_addresses: []
    });
  };

  const selectWalletsWithBUYT = () => {
    const tokenAddress = config.token_address || 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf';
    const walletsWithBUYT = availableWallets.filter(w => {
      const buytBalance = w.jetton_balances?.[tokenAddress] || 0;
      return buytBalance > 0;
    }).map(w => w.address);
    
    setConfig({
      ...config,
      grid_trading_wallet_addresses: walletsWithBUYT
    });
  };

  const selectWalletsWithTON = () => {
    const walletsWithTON = availableWallets.filter(w => (w.ton_balance || 0) > 0).map(w => w.address);
    setConfig({
      ...config,
      grid_trading_wallet_addresses: walletsWithTON
    });
  };

  const selectEmptyWallets = () => {
    const tokenAddress = config.token_address || 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf';
    const emptyWallets = availableWallets.filter(w => {
      const buytBalance = w.jetton_balances?.[tokenAddress] || 0;
      const tonBalance = w.ton_balance || 0;
      return buytBalance === 0 && tonBalance === 0;
    }).map(w => w.address);
    
    setConfig({
      ...config,
      grid_trading_wallet_addresses: emptyWallets
    });
  };

  const selectWalletsByGroup = (groupName) => {
    console.log('Selecting wallets for group:', groupName);
    
    const groupWallets = availableWallets.filter(w => {
      const wGroupName = w.group_name || 'No Group';
      return wGroupName === groupName;
    }).map(w => w.address);
    
    console.log('Found wallets:', groupWallets.length);
    
    setConfig({
      ...config,
      grid_trading_wallet_addresses: groupWallets
    });
  };

  const getWalletGroups = () => {
    const groups = {};
    availableWallets.forEach(wallet => {
      const groupName = wallet.group_name || 'No Group';
      if (!groups[groupName]) {
        groups[groupName] = {
          name: groupName,
          count: 0,
          totalTON: 0,
          totalBUYT: 0
        };
      }
      groups[groupName].count++;
      groups[groupName].totalTON += (wallet.ton_balance || 0) / 1e9;
      
      const tokenAddress = config.token_address || 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf';
      const buytBalance = wallet.jetton_balances?.[tokenAddress] || 0;
      groups[groupName].totalBUYT += buytBalance / 1e9;
    });
    return Object.values(groups);
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/config`, config);
      toast.success('Configuration saved successfully');
      await loadConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const startStrategy = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/start`);
      toast.success('Market maker strategy started!');
      await loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start strategy');
    } finally {
      setLoading(false);
    }
  };

  const stopStrategy = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/stop`);
      toast.success('Market maker strategy stopped');
      await loadStatus();
    } catch (error) {
      toast.error('Failed to stop strategy');
    } finally {
      setLoading(false);
    }
  };

  // Strategy management functions
  const createStrategy = async (strategyData) => {
    try {
      const response = await axios.post(`${API_URL}/api/market-maker/strategies`, strategyData);
      if (response.data.success) {
        toast.success('Strategy created successfully');
        // Reload strategies and wait for state update
        const updatedStrategies = await loadStrategies();
        console.log('Strategies after creation:', updatedStrategies.length);
        // Small delay to ensure React has updated the state before closing dialog
        await new Promise(resolve => setTimeout(resolve, 200));
        setShowStrategyDialog(false);
        setEditingStrategy(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create strategy');
    }
  };

  const updateStrategy = async (strategyId, strategyData) => {
    try {
      const response = await axios.put(`${API_URL}/api/market-maker/strategies/${strategyId}`, strategyData);
      if (response.data.success) {
        toast.success('Strategy updated successfully');
        await loadStrategies();
        // Small delay to ensure React has updated the state before closing dialog
        await new Promise(resolve => setTimeout(resolve, 100));
        setShowStrategyDialog(false);
        setEditingStrategy(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update strategy');
    }
  };

  const deleteStrategy = async (strategyId) => {
    if (!confirm('Are you sure you want to delete this strategy?')) {
      return;
    }
    try {
      const response = await axios.delete(`${API_URL}/api/market-maker/strategies/${strategyId}`);
      if (response.data.success) {
        toast.success('Strategy deleted successfully');
        await loadStrategies();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete strategy');
    }
  };

  const toggleStrategyEnabled = async (strategyId, enabled) => {
    try {
      const strategy = strategies.find(s => s.id === strategyId);
      if (!strategy) return;
      
      const updatedStrategy = { ...strategy, enabled: !enabled };
      const response = await axios.put(`${API_URL}/api/market-maker/strategies/${strategyId}`, updatedStrategy);
      if (response.data.success) {
        toast.success(`Strategy ${enabled ? 'disabled' : 'enabled'}`);
        await loadStrategies();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle strategy');
    }
  };

  const handleEditStrategy = async (strategy) => {
    setEditingStrategy(strategy);
    // Ensure groups are loaded before opening dialog
    await loadWalletGroups();
    setShowStrategyDialog(true);
  };

  const handleNewStrategy = async () => {
    setEditingStrategy(null);
    // Ensure groups are loaded before opening dialog
    await loadWalletGroups();
    setShowStrategyDialog(true);
  };

  // Price Holding functions
  const loadPriceHoldingConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/price-holding/config`);
      if (response.data.success) {
        setPriceHoldingConfig(response.data.config);
        if (response.data.current_price) {
          setPriceHoldingStatus(prev => ({
            ...prev,
            current_price: response.data.current_price
          }));
        }
      }
    } catch (error) {
      console.error('Error loading price holding config:', error);
    }
  };

  const loadPriceHoldingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market-maker/price-holding/status`);
      if (response.data.success) {
        setPriceHoldingStatus({
          current_price: response.data.current_price,
          at_floor: response.data.at_floor,
          at_ceiling: response.data.at_ceiling
        });
      }
    } catch (error) {
      console.error('Error loading price holding status:', error);
    }
  };

  const savePriceHoldingConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/market-maker/price-holding/config`, priceHoldingConfig);
      toast.success('Price holding configuration saved!');
      await loadPriceHoldingConfig();
    } catch (error) {
      toast.error('Failed to save price holding configuration');
    } finally {
      setLoading(false);
    }
  };

  // Reload strategies when dialog closes
  useEffect(() => {
    if (!showStrategyDialog && activeTab === 'strategy') {
      // Dialog was closed, reload strategies to ensure list is up to date
      loadStrategies();
    }
  }, [showStrategyDialog, activeTab]);

  // Load price holding data when tab is active
  useEffect(() => {
    if (activeTab === 'priceHolding') {
      loadPriceHoldingConfig();
      loadPriceHoldingStatus();
      
      const interval = setInterval(() => {
        loadPriceHoldingStatus();
      }, 5000);
      
      return () => clearInterval(interval);
    } else if (activeTab === 'logs') {
      loadLogs();
      
      const interval = setInterval(() => {
        loadLogs();
      }, 3000); // Refresh logs every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const categorizedLogs = useMemo(() => {
    const categories = {
      all: [],
      actions: [],
      errors: [],
      info: []
    };

    logs.forEach((entry) => {
      const normalizedEntry = {
        ...entry,
        level: (entry.level || 'INFO').toUpperCase()
      };

      categories.all.push(normalizedEntry);

      if (normalizedEntry.level === 'SUCCESS') {
        categories.actions.push(normalizedEntry);
      } else if (normalizedEntry.level === 'ERROR') {
        categories.errors.push(normalizedEntry);
      } else {
        categories.info.push(normalizedEntry);
      }
    });

    return categories;
  }, [logs]);

  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) {
      return '—';
    }
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }
    return date.toLocaleString();
  };

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 'SUCCESS':
        return 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
      case 'ERROR':
        return 'border border-red-500/40 bg-red-500/10 text-red-300';
      case 'WARNING':
        return 'border border-yellow-500/40 bg-yellow-500/10 text-yellow-300';
      default:
        return 'border border-blue-500/40 bg-blue-500/10 text-blue-300';
    }
  };

  const renderAllLogsTable = (entries) => {
    if (!entries || entries.length === 0) {
      return (
        <div className="rounded-lg border border-gray-800 bg-gray-950/80 p-10 text-center text-sm text-gray-500">
          Нет логов. Запустите стратегию, чтобы увидеть новые записи.
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-lg border border-gray-800 bg-gray-950/80">
        <div className="grid grid-cols-[160px_120px_1fr] border-b border-gray-800 bg-gray-900/60 px-4 py-2 text-[11px] uppercase tracking-wide text-gray-400">
          <span>Время</span>
          <span>Категория</span>
          <span>Сообщение</span>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {entries.map((entry, idx) => (
            <div
              key={entry.id || idx}
              className="grid grid-cols-[160px_120px_1fr] border-b border-gray-900/40 px-4 py-3 text-sm text-gray-100 hover:bg-gray-900/40"
            >
              <div className="font-mono text-xs text-gray-300">
                {formatLogTimestamp(entry.timestamp)}
              </div>
              <div>
                <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClass(entry.level)}`}>
                  {entry.level}
                </span>
              </div>
              <div className="whitespace-pre-wrap text-xs leading-relaxed md:text-sm">
                {entry.message}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCategoryPanel = (title, description, entries, accentClass) => (
    <div className="rounded-lg border border-gray-800 bg-gray-950/80 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-200">{title}</div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
        <span className={`rounded-full border border-gray-800 px-3 py-1 text-xs font-semibold ${accentClass}`}>
          {entries.length}
        </span>
      </div>
      {entries.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-gray-800 bg-gray-900/40 p-4 text-center text-xs text-gray-500">
          Нет записей в этой категории
        </div>
      ) : (
        <div className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="rounded-md border border-gray-800 bg-gray-950/90 p-3 text-gray-100"
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-gray-400">
                <span className="font-mono normal-case text-xs text-gray-300">
                  {formatLogTimestamp(entry.timestamp)}
                </span>
                <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClass(entry.level)}`}>
                  {entry.level}
                </span>
              </div>
              <div className="mt-2 whitespace-pre-wrap text-xs leading-relaxed md:text-sm">
                {entry.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Market Maker Strategy</h1>
          <p className="text-gray-500 mt-1">Automated counter-trading against enemy wallets</p>
        </div>
        <Badge 
          variant={status.running ? 'default' : 'secondary'} 
          className={`text-lg px-4 py-2 ${status.running ? 'bg-green-600' : ''}`}
        >
          {status.running ? (
            <>
              <Activity className="h-4 w-4 mr-2 animate-pulse" />
              RUNNING
            </>
          ) : (
            'STOPPED'
          )}
        </Badge>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Operations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_operations}</div>
            <p className="text-xs text-gray-500 mt-1">
              ✓ {stats.successful_operations} success, ✗ {stats.failed_operations} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.total_volume_usd.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">
              ↑ {stats.buys} buys, ↓ {stats.sells} sells
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Profit/Loss</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.total_pnl_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${Math.abs(stats.total_pnl_usd).toFixed(2)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {stats.total_pnl_usd >= 0 ? 'Profit' : 'Loss'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.total_operations > 0 
                ? ((stats.successful_operations / stats.total_operations) * 100).toFixed(1)
                : 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Overall success rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('strategy')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'strategy'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Counter-Trading Strategy
        </button>
        <button
          onClick={() => setActiveTab('priceHolding')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'priceHolding'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Price Holding
        </button>
        <button
          onClick={() => setActiveTab('random')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'random'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Random Pulses
        </button>
        <button
          onClick={() => setActiveTab('gridTrading')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'gridTrading'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Grid Trading
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'logs'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          System Logs
        </button>
      </div>

      {/* Strategy Tab */}
      {activeTab === 'strategy' && (
        <>
          {/* Strategies List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Counter-Trading Strategies
                  </CardTitle>
                  <CardDescription>
                    Управление параллельными стратегиями для разных групп кошельков
                  </CardDescription>
                </div>
                <Button onClick={handleNewStrategy} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Создать стратегию
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {strategies.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Нет стратегий. Создайте первую стратегию для начала работы.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {strategies.map((strategy) => (
                    <Card key={strategy.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{strategy.name}</h3>
                              <Badge variant={strategy.enabled ? "default" : "secondary"}>
                                {strategy.enabled ? "Включена" : "Выключена"}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                              <div>
                                <p className="text-gray-500">Группа кошельков</p>
                                <p className="font-medium flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {strategy.wallet_group_name || "Все кошельки"}
                                </p>
                                {strategy.wallet_group_name && (() => {
                                  const groupData = walletGroups.find(g => 
                                    (typeof g === 'object' ? g.group_name : g) === strategy.wallet_group_name
                                  );
                                  if (typeof groupData === 'object' && groupData) {
                                    return (
                                      <p className="text-xs text-gray-400 mt-1">
                                        {groupData.count} кошельков • {groupData.total_ton_readable?.toFixed(2) || '0.00'} TON • {groupData.total_buyt_readable?.toFixed(2) || '0.00'} BUYT
                                      </p>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <div>
                                <p className="text-gray-500">Min Volume</p>
                                <p className="font-medium">${strategy.min_enemy_volume_usd}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Sell (Enemy Buy)</p>
                                <p className="font-medium">{strategy.enemy_buy_sell_min_percent}% - {strategy.enemy_buy_sell_max_percent}%</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Buy (Enemy Sell)</p>
                                <p className="font-medium">{strategy.enemy_sell_buy_min_percent}% - {strategy.enemy_sell_buy_max_percent}%</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleStrategyEnabled(strategy.id, strategy.enabled)}
                            >
                              {strategy.enabled ? "Выключить" : "Включить"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditStrategy(strategy)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteStrategy(strategy.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Strategy Dialog */}
          <Dialog open={showStrategyDialog} onOpenChange={setShowStrategyDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingStrategy ? "Редактировать стратегию" : "Создать стратегию"}
                </DialogTitle>
                <DialogDescription>
                  Настройка параметров counter-trading стратегии для группы кошельков
                </DialogDescription>
              </DialogHeader>
              <StrategyForm
                strategy={editingStrategy}
                walletGroups={walletGroups}
                onSubmit={(data) => {
                  if (editingStrategy) {
                    updateStrategy(editingStrategy.id, data);
                  } else {
                    createStrategy(data);
                  }
                }}
                onCancel={() => {
                  setShowStrategyDialog(false);
                  setEditingStrategy(null);
                }}
              />
            </DialogContent>
          </Dialog>

          {/* Legacy Config (for backward compatibility) */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Legacy Strategy Configuration
              </CardTitle>
              <CardDescription>
                Старая конфигурация (для обратной совместимости)
              </CardDescription>
            </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Enable Strategy</p>
              <p className="text-sm text-gray-500">Turn strategy on/off</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setConfig({...config, enabled: !config.enabled})}
                variant={config.enabled ? 'default' : 'outline'}
                size="sm"
              >
                {config.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          </div>

          {/* Min Enemy Volume */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Minimum Enemy Volume (USD)
            </label>
            <Input
              type="number"
              value={config.min_enemy_volume_usd}
              onChange={(e) => setConfig({...config, min_enemy_volume_usd: parseFloat(e.target.value)})}
              step="1"
              min="1"
            />
            <p className="text-xs text-gray-500">Only react to enemy swaps above this volume</p>
          </div>

          {/* Reaction Delay */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Reaction Delay (seconds)
            </label>
            <Input
              type="number"
              value={config.reaction_delay_seconds}
              onChange={(e) => setConfig({...config, reaction_delay_seconds: parseFloat(e.target.value)})}
              step="0.5"
              min="0"
            />
            <p className="text-xs text-gray-500">Wait time before executing counter-operation</p>
          </div>

          {/* Enemy BUY -> We SELL */}
          <div className="p-4 bg-red-50 rounded-lg space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />
              When Enemy BUYS → We SELL
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm">Min % to Sell</label>
                <Input
                  type="number"
                  value={config.enemy_buy_sell_min_percent}
                  onChange={(e) => setConfig({...config, enemy_buy_sell_min_percent: parseFloat(e.target.value)})}
                  step="5"
                  min="0"
                  max="100"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Max % to Sell</label>
                <Input
                  type="number"
                  value={config.enemy_buy_sell_max_percent}
                  onChange={(e) => setConfig({...config, enemy_buy_sell_max_percent: parseFloat(e.target.value)})}
                  step="5"
                  min="0"
                  max="200"
                />
              </div>
            </div>
            <p className="text-xs text-gray-600">
              Random % between min and max of enemy's volume
              <br />
              <strong>💡 Tip:</strong> Set both to 0 to disable this counter-measure
            </p>
          </div>

          {/* Enemy SELL -> We BUY */}
          <div className="p-4 bg-green-50 rounded-lg space-y-3">
            <h3 className="font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              When Enemy SELLS → We BUY
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm">Min % to Buy</label>
                <Input
                  type="number"
                  value={config.enemy_sell_buy_min_percent}
                  onChange={(e) => setConfig({...config, enemy_sell_buy_min_percent: parseFloat(e.target.value)})}
                  step="5"
                  min="0"
                  max="100"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Max % to Buy</label>
                <Input
                  type="number"
                  value={config.enemy_sell_buy_max_percent}
                  onChange={(e) => setConfig({...config, enemy_sell_buy_max_percent: parseFloat(e.target.value)})}
                  step="5"
                  min="0"
                  max="200"
                />
              </div>
            </div>
            <p className="text-xs text-gray-600">
              Random % between min and max of enemy's volume
              <br />
              <strong>💡 Tip:</strong> Set both to 0 to disable this counter-measure
            </p>
          </div>

          {/* Distribution Settings */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4" />
              Split Threshold (USD)
            </label>
            <Input
              type="number"
              value={config.split_threshold_usd}
              onChange={(e) => setConfig({...config, split_threshold_usd: parseFloat(e.target.value)})}
              step="10"
              min="1"
            />
            <p className="text-xs text-gray-500">Above this amount, split operation across multiple wallets</p>
          </div>

          {/* Max Per Operation */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Max Per Operation (USD)
            </label>
            <Input
              type="number"
              value={config.max_per_operation_usd}
              onChange={(e) => setConfig({...config, max_per_operation_usd: parseFloat(e.target.value)})}
              step="50"
              min="10"
            />
            <p className="text-xs text-gray-500">Maximum USD amount for a single operation</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={saveConfig} disabled={loading} className="flex-1">
              <Settings className="h-4 w-4 mr-2" />
              Save Configuration
            </Button>
            
            {!status.running ? (
              <Button onClick={startStrategy} disabled={loading || !config.enabled} className="flex-1 bg-green-600 hover:bg-green-700">
                <Play className="h-4 w-4 mr-2" />
                Start Strategy
              </Button>
            ) : (
              <Button onClick={stopStrategy} disabled={loading} variant="destructive" className="flex-1">
                <Pause className="h-4 w-4 mr-2" />
                Stop Strategy
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Operation History
          </CardTitle>
          <CardDescription>Recent automated trades</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading && history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
              <p>Loading operation history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No operations yet. Start the strategy to begin trading.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Enemy Action</TableHead>
                  <TableHead>Enemy Volume</TableHead>
                  <TableHead>Enemy Amount</TableHead>
                  <TableHead>Enemy Price</TableHead>
                  <TableHead>Enemy TX</TableHead>
                  <TableHead>Our Action</TableHead>
                  <TableHead>Our Volume</TableHead>
                  <TableHead>Wallets</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((entry) => {
                  const share = entry.enemy_volume_usd > 0
                    ? Math.min(entry.our_volume_usd || 0, entry.enemy_volume_usd) / entry.enemy_volume_usd
                    : 0;
                  const showResponse = entry.our_action && (entry.our_volume_usd || entry.our_wallets?.length);

                  return (
                    <React.Fragment key={entry.id || entry.enemy_tx_hash || `entry-${entry.timestamp}`}>
                      <TableRow className="border-b border-slate-800">
                        <TableCell className="font-mono text-xs">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </TableCell>
                        <TableCell>
                          {entry.enemy_action ? (
                            <Badge
                              variant={entry.enemy_action.toLowerCase() === 'buy' ? 'default' : 'secondary'}
                              className={entry.enemy_action.toLowerCase() === 'buy' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                            >
                              {entry.enemy_action.toUpperCase()}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Unknown</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          ${typeof entry.enemy_volume_usd === 'number' ? entry.enemy_volume_usd.toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {entry.enemy_token_amount && typeof entry.enemy_token_amount === 'number'
                            ? `${(entry.enemy_token_amount / 1000000).toFixed(2)}M`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {entry.enemy_price && typeof entry.enemy_price === 'number' && entry.enemy_price > 0
                            ? `$${entry.enemy_price.toFixed(9)}`
                            : (entry.enemy_volume_usd && entry.enemy_token_amount && typeof entry.enemy_volume_usd === 'number' && typeof entry.enemy_token_amount === 'number' && entry.enemy_token_amount > 0
                              ? `$${(entry.enemy_volume_usd / entry.enemy_token_amount).toFixed(9)}`
                              : '-')}
                        </TableCell>
                        <TableCell>
                          {entry.enemy_tx_hash ? (
                            <a
                              href={`https://tonviewer.com/transaction/${entry.enemy_tx_hash.split('_')[0]}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 hover:underline text-xs font-mono transition-colors"
                              title={`View transaction: ${entry.enemy_tx_hash.split('_')[0]}`}
                            >
                              {entry.enemy_tx_hash.split('_')[0].substring(0, 8)}...
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.our_action ? (
                            <Badge
                              variant={entry.our_action.toLowerCase() === 'buy' ? 'default' : 'secondary'}
                              className={entry.our_action.toLowerCase() === 'buy' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                            >
                              {entry.our_action.toUpperCase()}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          ${typeof entry.our_volume_usd === 'number' ? entry.our_volume_usd.toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell>
                          {entry.our_wallets && Array.isArray(entry.our_wallets) ? entry.our_wallets.length : 0} wallet(s)
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-300">
                              {entry.status === 'BUYCRYPT' ? 'BUYCRYPT' : (entry.status || 'unknown')}
                            </span>
                            <div className="h-2 w-full rounded-full bg-slate-900">
                              <div
                                className="h-2 rounded-full bg-emerald-400 transition-all duration-200"
                                style={{ width: `${(share * 100).toFixed(1)}%` }}
                              />
                            </div>
                            <span className="text-[10px] uppercase text-slate-500">Intensity {(share * 100).toFixed(0)}%</span>
                          </div>
                        </TableCell>
                      </TableRow>

                      {showResponse && (
                        <TableRow className="bg-sky-950/40">
                          <TableCell colSpan={10} className="px-6 py-3">
                            <div className="flex flex-col gap-2 md:items-center md:flex-row md:justify-between">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-blue-500 text-white">Our Response</Badge>
                                <span className="text-xs text-slate-300">
                                  {entry.our_action ? `${entry.our_action.toUpperCase()}${entry.our_volume_usd ? ` $${entry.our_volume_usd.toFixed(2)}` : ''}` : '—'}
                                </span>
                              </div>
                              <div className="flex flex-col text-right text-[11px] text-slate-400">
                                <span>
                                  Wallets: {entry.our_wallets && Array.isArray(entry.our_wallets) ? entry.our_wallets.length : 0}
                                </span>
                                <span>
                                  {entry.enemy_volume_usd && !Number.isNaN(entry.enemy_volume_usd)
                                    ? `${(share * 100).toFixed(0)}% of enemy volume`
                                    : '— of enemy volume'}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* Price Holding Tab */}
      {activeTab === 'priceHolding' && (
        <>
          {/* Current Price Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Price Holding Strategy
              </CardTitle>
              <CardDescription>
                Automatically buy when price drops below floor and sell when it rises above ceiling
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="bg-blue-50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-1">Current Price</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ${priceHoldingStatus.current_price?.toFixed(8) || '0.00000000'}
                    </p>
                  </CardContent>
                </Card>
                <Card className={priceHoldingStatus.at_floor ? 'bg-red-50' : ''}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-1">Price Floor</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${priceHoldingConfig.min_price_usd?.toFixed(8)}
                    </p>
                    {priceHoldingStatus.at_floor && (
                      <Badge variant="destructive" className="mt-2">At Floor!</Badge>
                    )}
                  </CardContent>
                </Card>
                <Card className={priceHoldingStatus.at_ceiling ? 'bg-green-50' : ''}>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-600 mb-1">Price Ceiling</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${priceHoldingConfig.max_price_usd?.toFixed(8)}
                    </p>
                    {priceHoldingStatus.at_ceiling && (
                      <Badge variant="default" className="bg-green-600 mt-2">At Ceiling!</Badge>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Configuration Form */}
              <div className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Enable Price Holding</p>
                    <p className="text-sm text-gray-500">Automatically maintain price within range</p>
                  </div>
                  <Button
                    onClick={() => setPriceHoldingConfig({...priceHoldingConfig, enabled: !priceHoldingConfig.enabled})}
                    variant={priceHoldingConfig.enabled ? 'default' : 'outline'}
                    size="sm"
                    className={priceHoldingConfig.enabled ? 'bg-green-600' : ''}
                  >
                    {priceHoldingConfig.enabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>

                {/* Price Floor Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-red-600">
                    <TrendingDown className="h-4 w-4" />
                    Price Floor (Buy Support)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Min Price (USD)</label>
                      <Input
                        type="number"
                        step="0.00000001"
                        value={priceHoldingConfig.min_price_usd}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, min_price_usd: parseFloat(e.target.value)})}
                        placeholder="0.00012"
                      />
                      <p className="text-xs text-gray-500 mt-1">Buy when price falls below this level</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Buy Volume (%)</label>
                      <Input
                        type="number"
                        step="1"
                        value={priceHoldingConfig.buy_volume_percent}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, buy_volume_percent: parseFloat(e.target.value)})}
                        placeholder="50"
                      />
                      <p className="text-xs text-gray-500 mt-1">% of sell volume to buy back</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Total Buy (USD)</label>
                      <Input
                        type="number"
                        step="10"
                        value={priceHoldingConfig.max_total_buy_usd}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, max_total_buy_usd: parseFloat(e.target.value)})}
                        placeholder="500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum to spend per session</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={priceHoldingConfig.stop_at_floor}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, stop_at_floor: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <label className="text-sm">Stop buying if price breaks floor (protection)</label>
                    </div>
                  </div>
                </div>

                {/* Price Ceiling Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-green-600">
                    <TrendingUp className="h-4 w-4" />
                    Price Ceiling (Sell at Profit)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Price (USD)</label>
                      <Input
                        type="number"
                        step="0.00000001"
                        value={priceHoldingConfig.max_price_usd}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, max_price_usd: parseFloat(e.target.value)})}
                        placeholder="0.00015"
                      />
                      <p className="text-xs text-gray-500 mt-1">Start selling when price rises above</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Sell Levels</label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        max="10"
                        value={priceHoldingConfig.sell_levels}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, sell_levels: parseInt(e.target.value)})}
                        placeholder="4"
                      />
                      <p className="text-xs text-gray-500 mt-1">Number of price levels to sell at</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Sell Volume per Level (%)</label>
                      <Input
                        type="number"
                        step="1"
                        value={priceHoldingConfig.sell_volume_percent}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, sell_volume_percent: parseFloat(e.target.value)})}
                        placeholder="25"
                      />
                      <p className="text-xs text-gray-500 mt-1">% of holdings to sell at each level</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Max Total Sell (%)</label>
                      <Input
                        type="number"
                        step="1"
                        value={priceHoldingConfig.max_total_sell_percent}
                        onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, max_total_sell_percent: parseFloat(e.target.value)})}
                        placeholder="80"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum % of total holdings to sell</p>
                    </div>
                  </div>
                </div>

                {/* Timing Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      Check Interval (seconds)
                    </label>
                    <Input
                      type="number"
                      step="5"
                      value={priceHoldingConfig.check_interval_seconds}
                      onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, check_interval_seconds: parseFloat(e.target.value)})}
                      placeholder="30"
                    />
                    <p className="text-xs text-gray-500 mt-1">How often to check price</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <Clock className="h-4 w-4 inline mr-1" />
                      React Delay (seconds)
                    </label>
                    <Input
                      type="number"
                      step="1"
                      value={priceHoldingConfig.react_delay_seconds}
                      onChange={(e) => setPriceHoldingConfig({...priceHoldingConfig, react_delay_seconds: parseFloat(e.target.value)})}
                      placeholder="5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Delay before executing orders</p>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={savePriceHoldingConfig}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Save Price Holding Config
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Strategy Description */}
          <Card>
            <CardHeader>
              <CardTitle>📊 Strategy Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-red-600 mb-2">🔻 Price Floor (Support)</h4>
                <p className="text-sm text-gray-600">
                  When token price drops below Min Price, bot automatically buys to provide support.
                  Buying stops if price breaks floor by 1% (protection against dumps).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-green-600 mb-2">🔺 Price Ceiling (Take Profit)</h4>
                <p className="text-sm text-gray-600">
                  When price rises above Max Price, bot sells in levels. For example with 4 levels:
                  Level 1 at $0.00015 (+0%), Level 2 at +5%, Level 3 at +10%, Level 4 at +15%.
                </p>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <p className="text-sm">
                  <strong>⚠️ Note:</strong> Price is tracked from Operation History. Make sure Counter-Trading strategy 
                  is running to collect price data, or there's recent trading activity.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'random' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Random Pulse Automation</CardTitle>
              <CardDescription>
                Автоматические “импульсы” по заданным диапазонам, генерирующие серии сделок и их реакции
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-blue-600"
                    checked={randomConfig?.enabled || false}
                    onChange={(e) => {
                      if (!randomConfig) return;
                      setRandomConfig({ ...randomConfig, enabled: e.target.checked });
                    }}
                  />
                  Enabled
                </label>
                <span className="text-xs text-gray-500">
                  {randomStatus?.status === 'running' ? 'Currently running' : 'Idle'}
                </span>
              </div>

              {randomLoading && !randomConfig ? (
                <p className="text-xs text-gray-500">Loading random configuration...</p>
              ) : (
                randomConfig && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Interval Min (s)
                      <Input
                        type="number"
                        value={randomConfig.interval_min_seconds}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          interval_min_seconds: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Interval Max (s)
                      <Input
                        type="number"
                        value={randomConfig.interval_max_seconds}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          interval_max_seconds: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Steps Min
                      <Input
                        type="number"
                        value={randomConfig.steps_min}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          steps_min: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Steps Max
                      <Input
                        type="number"
                        value={randomConfig.steps_max}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          steps_max: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Volume Min (USD)
                      <Input
                        type="number"
                        value={randomConfig.volume_min_usd}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          volume_min_usd: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Volume Max (USD)
                      <Input
                        type="number"
                        value={randomConfig.volume_max_usd}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          volume_max_usd: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Trend Bias (%)
                      <Input
                        type="number"
                        value={randomConfig.trend_bias_percent}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          trend_bias_percent: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Split Threshold (USD)
                      <Input
                        type="number"
                        value={randomConfig.split_threshold_usd}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          split_threshold_usd: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Max Ops / Minute
                      <Input
                        type="number"
                        value={randomConfig.max_ops_per_minute}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          max_ops_per_minute: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Max Concurrent Series
                      <Input
                        type="number"
                        value={randomConfig.max_concurrent_series}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          max_concurrent_series: Number(e.target.value)
                        })}
                      />
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Primary Wallet Group
                      <select
                        className="rounded border border-slate-700 bg-slate-900 text-white p-2 text-sm"
                        value={randomConfig.primary_wallet_group || ''}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          primary_wallet_group: e.target.value || null
                        })}
                      >
                        <option value="">All wallets</option>
                        {walletGroups.map((group) => {
                          const name = typeof group === 'object' ? group.group_name : group;
                          return (
                            <option key={`group-${name}`} value={name}>
                              {name}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                    <label className="text-xs text-gray-500 flex flex-col gap-1">
                      Reaction Wallet Group
                      <select
                        className="rounded border border-slate-700 bg-slate-900 text-white p-2 text-sm"
                        value={randomConfig.reaction_wallet_group || ''}
                        onChange={(e) => setRandomConfig({
                          ...randomConfig,
                          reaction_wallet_group: e.target.value || null
                        })}
                      >
                        <option value="">Use primary</option>
                        {walletGroups.map((group) => {
                          const name = typeof group === 'object' ? group.group_name : group;
                          return (
                            <option key={`reaction-${name}`} value={name}>
                              {name}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  </div>
                )
              )}

              <div className="mt-4 flex items-center gap-3">
                <Button onClick={saveRandomConfig} disabled={randomSaving || !randomConfig}>
                  {randomSaving ? 'Saving...' : 'Save Random Settings'}
                </Button>
                <Button
                  variant="outline"
                  onClick={loadRandomConfig}
                  disabled={randomLoading}
                >
                  Reload
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Random Pulse Status</CardTitle>
              <CardDescription>Автоматически генерируемые серии и их результаты</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-100">Status:</span>
                  <Badge
                    className={`px-2 py-1 text-xs ${
                      randomStatus?.status === 'running' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-300'
                    }`}
                  >
                    {randomStatus?.status || 'idle'}
                  </Badge>
                </div>
                <div>
                  Next Run:{' '}
                  {randomStatus?.next_run_at
                    ? new Date(randomStatus.next_run_at).toLocaleTimeString()
                    : '—'}
                </div>
                <div>Current Template: {randomStatus?.template || 'N/A'}</div>
                <div>Current Step: {randomStatus?.current_step || '—'}</div>
                {randomStatus?.last_series_summary && (
                  <div className="mt-2 text-xs text-slate-400">
                    Last Series ({randomStatus.last_series_summary.series_id}):{' '}
                    {randomStatus.last_series_summary.operations} ops (
                    {randomStatus.last_series_summary.successes} success,{' '}
                    {randomStatus.last_series_summary.failures} fail) · Completed at{' '}
                    {new Date(randomStatus.last_series_summary.completed_at).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      {/* Grid Trading Tab */}
      {activeTab === 'gridTrading' && (
        <>
          {/* Status and Control Panel */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Grid Trading
                  </CardTitle>
                  <CardDescription>
                    Автоматическая торговля по сетке уровней цены
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {seriesStatus.status === 'ACTIVE' ? (
                    <Button 
                      onClick={stopSeries}
                      variant="destructive"
                      className="flex items-center gap-2"
                    >
                      <Pause className="h-4 w-4" />
                      Остановить
                    </Button>
                  ) : (
                    <Button 
                      onClick={startSeries}
                      className="flex items-center gap-2"
                    >
                      <Play className="h-4 w-4" />
                      Запустить
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Статус:</span>
                    <Badge className={seriesStatus.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'}>
                      {seriesStatus.status === 'ACTIVE' ? 'Активна' : 'Остановлена'}
                    </Badge>
                  </div>
                  {seriesStatus.status === 'ACTIVE' && (
                    <>
                      <div className={`text-sm font-bold ${seriesStatus.profit_loss_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        P&L: ${seriesStatus.profit_loss_usd?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-sm text-gray-600">
                        Покупок: {seriesStatus.total_buys || 0} | Продаж: {seriesStatus.total_sells || 0}
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {seriesStatus.status === 'ACTIVE' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-gray-600">Базовая цена</div>
                    <div className="font-bold text-blue-600">${seriesStatus.base_price_usd?.toFixed(6) || '0.000000'}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Последняя продажа</div>
                    <div className="font-bold text-green-600">${seriesStatus.last_sell_price?.toFixed(6) || '0.000000'}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Последняя покупка</div>
                    <div className="font-bold text-red-600">${seriesStatus.last_buy_price?.toFixed(6) || '0.000000'}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Объем продаж</div>
                    <div className="font-bold">${seriesStatus.total_sell_volume_usd?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Main Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Настройки Grid Trading
              </CardTitle>
              <CardDescription>
                Выберите режим торговли и настройте параметры
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Grid Trading */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium">Включить Grid Trading</label>
                  <p className="text-xs text-gray-500 mt-1">Активировать автоматическую торговлю</p>
                </div>
                <input
                  type="checkbox"
                  checked={config.enable_grid_trading}
                  onChange={(e) => setConfig({...config, enable_grid_trading: e.target.checked})}
                  className="h-5 w-5"
                />
              </div>

              {config.enable_grid_trading && (
                <>
                  {/* Trading Mode Selection */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium block">Режим торговли</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setConfig({...config, grid_trading_mode: 'BOTH'})}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          config.grid_trading_mode === 'BOTH'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">Обе стороны</div>
                        <div className="text-xs text-gray-600">Покупка и продажа</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfig({...config, grid_trading_mode: 'BUY_ONLY'})}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          config.grid_trading_mode === 'BUY_ONLY'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">Только покупка</div>
                        <div className="text-xs text-gray-600">Только BUY операции</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfig({...config, grid_trading_mode: 'SELL_ONLY'})}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          config.grid_trading_mode === 'SELL_ONLY'
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm mb-1">Только продажа</div>
                        <div className="text-xs text-gray-600">Только SELL операции</div>
                      </button>
                    </div>
                  </div>

                  {/* SELL_ONLY Mode Settings */}
                  {config.grid_trading_mode === 'SELL_ONLY' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium">Продавать при покупке врага</label>
                          <p className="text-xs text-gray-600 mt-1">Реагировать на покупки врага</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={config.grid_sell_on_enemy_buy}
                          onChange={(e) => setConfig({...config, grid_sell_on_enemy_buy: e.target.checked})}
                          className="h-4 w-4"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Процент от покупки врага (%)
                        </label>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          value={config.grid_sell_percent_of_enemy}
                          onChange={(e) => setConfig({...config, grid_sell_percent_of_enemy: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Продавать {config.grid_sell_percent_of_enemy}% от объема каждой покупки врага
                        </p>
                      </div>
                      
                      {config.grid_sell_on_enemy_buy && (
                        <div>
                          <label className="text-sm font-medium block mb-2">
                            Минимальное изменение цены (%)
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0.1"
                            value={config.grid_sell_price_change_percent}
                            onChange={(e) => setConfig({...config, grid_sell_price_change_percent: parseFloat(e.target.value)})}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Продавать только если цена изменилась на {config.grid_sell_price_change_percent}% с момента последней продажи
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* BOTH Mode - Enemy Buy Reaction Settings */}
                  {config.grid_trading_mode === 'BOTH' && (
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium">Продавать при покупке врага</label>
                          <p className="text-xs text-gray-600 mt-1">Дополнительно: реагировать на покупки врага</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={config.grid_sell_on_enemy_buy}
                          onChange={(e) => setConfig({...config, grid_sell_on_enemy_buy: e.target.checked})}
                          className="h-4 w-4"
                        />
                      </div>
                      
                      {config.grid_sell_on_enemy_buy && (
                        <>
                          <div>
                            <label className="text-sm font-medium block mb-2">
                              Процент от покупки врага (%)
                            </label>
                            <Input
                              type="number"
                              step="1"
                              min="1"
                              max="100"
                              value={config.grid_sell_percent_of_enemy}
                              onChange={(e) => setConfig({...config, grid_sell_percent_of_enemy: parseFloat(e.target.value)})}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Продавать {config.grid_sell_percent_of_enemy}% от объема каждой покупки врага
                            </p>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium block mb-2">
                              Минимальное изменение цены (%)
                            </label>
                            <Input
                              type="number"
                              step="0.1"
                              min="0.1"
                              value={config.grid_sell_price_change_percent}
                              onChange={(e) => setConfig({...config, grid_sell_price_change_percent: parseFloat(e.target.value)})}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Продавать только если цена изменилась на {config.grid_sell_price_change_percent}% с момента последней продажи
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* BOTH or BUY_ONLY Mode Settings */}
                  {(config.grid_trading_mode === 'BOTH' || config.grid_trading_mode === 'BUY_ONLY') && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-4">
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Интервал покупки (%)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          value={config.grid_buy_interval_percent}
                          onChange={(e) => setConfig({...config, grid_buy_interval_percent: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Покупать каждые {config.grid_buy_interval_percent}% падения цены
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Сумма покупки ($)
                        </label>
                        <Input
                          type="number"
                          step="10"
                          value={config.grid_buy_amount_usd}
                          onChange={(e) => setConfig({...config, grid_buy_amount_usd: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Покупать на ${config.grid_buy_amount_usd} при каждом срабатывании
                        </p>
                      </div>
                    </div>
                  )}

                  {/* BOTH or SELL_ONLY Mode Settings (Grid-based selling) */}
                  {(config.grid_trading_mode === 'BOTH' || config.grid_trading_mode === 'SELL_ONLY') && !config.grid_sell_on_enemy_buy && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4">
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Интервал продажи (%)
                        </label>
                        <Input
                          type="number"
                          step="0.1"
                          value={config.grid_sell_interval_percent}
                          onChange={(e) => setConfig({...config, grid_sell_interval_percent: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Продавать каждые {config.grid_sell_interval_percent}% роста цены
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium block mb-2">
                          Процент от баланса (%)
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={config.grid_sell_amount_percent}
                          onChange={(e) => setConfig({...config, grid_sell_amount_percent: parseFloat(e.target.value)})}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Продавать {config.grid_sell_amount_percent}% от баланса BUYT при каждом срабатывании
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Min Transaction Size */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="text-sm font-medium block mb-2">
                      Множитель минимальной транзакции
                    </label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={config.min_transaction_multiplier}
                      onChange={(e) => setConfig({...config, min_transaction_multiplier: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Минимальный размер = комиссия × {config.min_transaction_multiplier}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Wallet Selection for Grid Trading */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Wallet Selection</CardTitle>
                  <CardDescription>
                    Choose which wallets to use for Grid Trading (empty = all wallets)
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={selectAllWallets}
                    variant="outline"
                    size="sm"
                  >
                    Select All
                  </Button>
                  <Button 
                    onClick={deselectAllWallets}
                    variant="outline"
                    size="sm"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {availableWallets.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  Loading wallets...
                </div>
              ) : (
                <>
                  {/* Quick Selection by Groups */}
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-700 mb-2">Quick Select by Group:</div>
                    <div className="flex gap-2 flex-wrap">
                      {getWalletGroups().map(group => {
                        const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                        return (
                          <button 
                            key={group.name}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              selectWalletsByGroup(group.name);
                            }}
                            className="border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="font-semibold text-sm">{group.name}</div>
                            <div className="text-[10px] text-gray-500 mt-1">
                              {group.count} wallets | TON: {formatNumber(group.totalTON)} | BUYT: {formatNumber(group.totalBUYT)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selection Summary */}
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      Selected: {config.grid_trading_wallet_addresses?.length || 0} / {availableWallets.length} wallets
                      {(!config.grid_trading_wallet_addresses || config.grid_trading_wallet_addresses.length === 0) && (
                        <span className="ml-2 text-blue-600 font-medium">(All wallets will be used)</span>
                      )}
                    </div>
                    {config.grid_trading_wallet_addresses?.length > 0 && (() => {
                      const tokenAddress = config.token_address || 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf';
                      const selectedWallets = availableWallets.filter(w => 
                        config.grid_trading_wallet_addresses.includes(w.address)
                      );
                      
                      const totalTON = selectedWallets.reduce((sum, w) => sum + (w.ton_balance || 0), 0) / 1e9;
                      const totalBUYT = selectedWallets.reduce((sum, w) => {
                        const buytBalance = w.jetton_balances?.[tokenAddress] || 0;
                        return sum + buytBalance;
                      }, 0) / 1e9;
                      
                      const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                      
                      return (
                        <div className="flex gap-4 text-xs">
                          <div>
                            <span className="text-gray-600">Total TON:</span>
                            <span className="ml-1 font-bold text-gray-900">{formatNumber(totalTON)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Total BUYT:</span>
                            <span className="ml-1 font-bold text-blue-600">{formatNumber(totalBUYT)}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                    {availableWallets.map((wallet) => {
                      // Get BUYT balance from jetton_balances
                      const tokenAddress = config.token_address || 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf';
                      const buytBalance = wallet.jetton_balances?.[tokenAddress] || 0;
                      const buytReadable = buytBalance / 1e9;
                      const tonReadable = (wallet.ton_balance || 0) / 1e9;
                      
                      // Format with thousand separators and no decimals
                      const formatNumber = (num) => {
                        return Math.floor(num).toLocaleString('en-US');
                      };
                      
                      return (
                        <div
                          key={wallet.address}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            config.grid_trading_wallet_addresses?.includes(wallet.address)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleWalletSelection(wallet.address)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-mono text-xs break-all">
                                {wallet.address}
                              </div>
                              <div className="mt-1 space-y-0.5">
                                <div className="text-xs text-gray-600">
                                  TON: {formatNumber(tonReadable)}
                                </div>
                                <div className={`text-xs font-medium ${buytReadable > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                  BUYT: {formatNumber(buytReadable)}
                                </div>
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={config.grid_trading_wallet_addresses?.includes(wallet.address) || false}
                              onChange={() => {}}
                              className="ml-2 h-4 w-4 flex-shrink-0"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Save Config Button */}
          <div className="flex justify-end">
            <Button 
              onClick={saveConfig}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              {loading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>

          {/* Series History */}
          {seriesStatus.status === 'ACTIVE' && seriesHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>
                  Last {seriesHistory.length} transactions in current series
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Price (USD)</TableHead>
                        <TableHead>Amount (BUYT)</TableHead>
                        <TableHead>Volume (USD)</TableHead>
                        <TableHead>Wallet</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seriesHistory.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="text-xs">
                            {new Date(tx.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={tx.transaction_type === 'BUY' ? 'bg-green-500' : 'bg-red-500'}>
                              {tx.transaction_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono">
                            ${tx.price_usd?.toFixed(6)}
                          </TableCell>
                          <TableCell className="font-mono">
                            {tx.amount_tokens?.toFixed(2)}
                          </TableCell>
                          <TableCell className="font-mono">
                            ${tx.amount_usd?.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-xs font-mono">
                            {tx.wallet_address?.substring(0, 10)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  System Logs
                </CardTitle>
                <CardDescription>
                  Real-time Market Maker operation logs
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={loadLogs}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                >
                  Refresh
                </Button>
                {status.running ? (
                  <Badge className="bg-green-600">
                    <Activity className="h-3 w-3 mr-1 animate-pulse" />
                    Running
                  </Badge>
                ) : (
                  <Badge variant="secondary">Stopped</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!status.running && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Market Maker is STOPPED</strong> - Strategy is not monitoring swaps. 
                  Click "Start Strategy" on Counter-Trading tab to begin.
                </p>
              </div>
            )}
            
            <div className="mt-4 space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">
                      Все события
                    </h3>
                    <p className="text-xs text-gray-500">
                      Последние {categorizedLogs.all.length} записей в порядке убывания времени
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">
                    Обновление каждые 3 сек • лимит {LOG_FETCH_LIMIT}
                  </span>
                </div>
                <div className="mt-3">
                  {renderAllLogsTable(categorizedLogs.all)}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">
                  Категории
                </h3>
                <p className="text-xs text-gray-500">
                  Отдельные списки для быстрого просмотра результатов, ошибок и служебных событий
                </p>
                <div className="mt-3 grid gap-4 md:grid-cols-3">
                  {renderCategoryPanel(
                    'Выполненные действия',
                    'Успешно завершённые операции и реакции',
                    categorizedLogs.actions,
                    'text-emerald-400'
                  )}
                  {renderCategoryPanel(
                    'Ошибки',
                    'Проблемы, требующие внимания',
                    categorizedLogs.errors,
                    'text-red-400'
                  )}
                  {renderCategoryPanel(
                    'Информативные',
                    'Информационные и предупредительные сообщения',
                    categorizedLogs.info,
                    'text-blue-400'
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Strategy Form Component
const StrategyForm = ({ strategy, walletGroups, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: strategy?.name || 'New Strategy',
    enabled: strategy?.enabled || false,
    wallet_group_name: strategy?.wallet_group_name || '',
    token_address: strategy?.token_address || 'EQCWibYIG72ifoJlXD39iXia-frDwTvA0aeL7-pmmgvj3dwf',
    min_enemy_volume_usd: strategy?.min_enemy_volume_usd || 10.0,
    reaction_delay_seconds: strategy?.reaction_delay_seconds || 5.0,
    enemy_buy_sell_min_percent: strategy?.enemy_buy_sell_min_percent || 50.0,
    enemy_buy_sell_max_percent: strategy?.enemy_buy_sell_max_percent || 100.0,
    enemy_sell_buy_min_percent: strategy?.enemy_sell_buy_min_percent || 50.0,
    enemy_sell_buy_max_percent: strategy?.enemy_sell_buy_max_percent || 100.0,
    split_threshold_usd: strategy?.split_threshold_usd || 50.0,
    max_per_operation_usd: strategy?.max_per_operation_usd || 1000.0,
    enable_price_filter: strategy?.enable_price_filter || false,
    min_sell_price_increase_percent: strategy?.min_sell_price_increase_percent || 2.0,
    min_buy_price_decrease_percent: strategy?.min_buy_price_decrease_percent || 2.0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Strategy Name */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Название стратегии</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
      </div>

      {/* Wallet Group */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Группа кошельков
        </label>
        <select
          className="w-full px-3 py-2 border rounded-md"
          value={formData.wallet_group_name || ''}
          onChange={(e) => setFormData({...formData, wallet_group_name: e.target.value || null})}
        >
          <option value="">Все кошельки</option>
          {walletGroups.length === 0 ? (
            <option disabled>Загрузка групп...</option>
          ) : (
            walletGroups.map((group) => {
              const groupName = typeof group === 'string' ? group : group.group_name;
              const groupData = typeof group === 'object' ? group : null;
              return (
                <option key={groupName} value={groupName}>
                  {groupName}
                  {groupData && (
                    ` (${groupData.count} кошельков, ${groupData.total_ton_readable?.toFixed(2) || '0.00'} TON, ${groupData.total_buyt_readable?.toFixed(2) || '0.00'} BUYT)`
                  )}
                </option>
              );
            })
          )}
        </select>
        <p className="text-xs text-gray-500">Выберите группу кошельков или оставьте пустым для всех</p>
        {/* Show detailed info for selected group */}
        {formData.wallet_group_name && walletGroups.find(g => (typeof g === 'object' ? g.group_name : g) === formData.wallet_group_name) && (() => {
          const selectedGroup = walletGroups.find(g => (typeof g === 'object' ? g.group_name : g) === formData.wallet_group_name);
          if (typeof selectedGroup === 'object') {
            return (
              <div className="p-3 bg-blue-50 rounded-md text-sm">
                <p className="font-medium mb-1">Информация о группе:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Кошельков:</span>
                    <span className="ml-2 font-medium">{selectedGroup.count}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">TON баланс:</span>
                    <span className="ml-2 font-medium">{selectedGroup.total_ton_readable?.toFixed(2) || '0.00'} TON</span>
                  </div>
                  <div>
                    <span className="text-gray-600">BUYT баланс:</span>
                    <span className="ml-2 font-medium">{selectedGroup.total_buyt_readable?.toFixed(2) || '0.00'} BUYT</span>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Enabled */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="enabled"
          checked={formData.enabled}
          onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
          className="h-4 w-4"
        />
        <label htmlFor="enabled" className="text-sm font-medium">Включить стратегию</label>
      </div>

      {/* Min Enemy Volume */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Минимальный объем врага (USD)
        </label>
        <Input
          type="number"
          value={formData.min_enemy_volume_usd}
          onChange={(e) => setFormData({...formData, min_enemy_volume_usd: parseFloat(e.target.value)})}
          step="1"
          min="1"
          required
        />
      </div>

      {/* Reaction Delay */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Задержка реакции (секунды)
        </label>
        <Input
          type="number"
          value={formData.reaction_delay_seconds}
          onChange={(e) => setFormData({...formData, reaction_delay_seconds: parseFloat(e.target.value)})}
          step="0.5"
          min="0"
          required
        />
      </div>

      {/* Enemy BUY -> We SELL */}
      <div className="p-4 bg-red-50 rounded-lg space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-red-600" />
          Когда враг ПОКУПАЕТ → Мы ПРОДАЕМ
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="text-sm">Min % продажи</label>
            <Input
              type="number"
              value={formData.enemy_buy_sell_min_percent}
              onChange={(e) => setFormData({...formData, enemy_buy_sell_min_percent: parseFloat(e.target.value)})}
              step="5"
              min="0"
              max="100"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm">Max % продажи</label>
            <Input
              type="number"
              value={formData.enemy_buy_sell_max_percent}
              onChange={(e) => setFormData({...formData, enemy_buy_sell_max_percent: parseFloat(e.target.value)})}
              step="5"
              min="0"
              max="200"
            />
          </div>
        </div>
      </div>

      {/* Enemy SELL -> We BUY */}
      <div className="p-4 bg-green-50 rounded-lg space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-green-600" />
          Когда враг ПРОДАЕТ → Мы ПОКУПАЕМ
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="text-sm">Min % покупки</label>
            <Input
              type="number"
              value={formData.enemy_sell_buy_min_percent}
              onChange={(e) => setFormData({...formData, enemy_sell_buy_min_percent: parseFloat(e.target.value)})}
              step="5"
              min="0"
              max="100"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm">Max % покупки</label>
            <Input
              type="number"
              value={formData.enemy_sell_buy_max_percent}
              onChange={(e) => setFormData({...formData, enemy_sell_buy_max_percent: parseFloat(e.target.value)})}
              step="5"
              min="0"
              max="200"
            />
          </div>
        </div>
      </div>

      {/* Distribution Settings */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4" />
            Порог разбиения (USD)
          </label>
          <Input
            type="number"
            value={formData.split_threshold_usd}
            onChange={(e) => setFormData({...formData, split_threshold_usd: parseFloat(e.target.value)})}
            step="10"
            min="1"
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Макс. на операцию (USD)
          </label>
          <Input
            type="number"
            value={formData.max_per_operation_usd}
            onChange={(e) => setFormData({...formData, max_per_operation_usd: parseFloat(e.target.value)})}
            step="50"
            min="10"
            required
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Отмена
        </Button>
        <Button type="submit" className="flex-1">
          {strategy ? "Сохранить" : "Создать"}
        </Button>
      </div>
    </form>
  );
};

export default MarketMaker;
